import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';

/**
 * Whether this workspace uses AI.
 *
 * This used to live in the browser's localStorage. It read as an account
 * setting — "AI features are active" — while being a preference belonging to
 * one browser on one device, so signing in on a phone showed it off however
 * many times it had been switched on elsewhere. Reported as exactly that.
 *
 * It belongs to the workspace, because that is what the spending belongs to,
 * and the server enforces it, because a gate the client draws is not a gate.
 *
 * Unknown means ON while loading. AI is a shipped feature that workspaces are
 * already using, and flashing everything off for a moment on every page load
 * would be its own kind of wrong.
 */
const FeatureFlagContext = createContext({
  aiEnabled: true,
  loading: true,
  setAiEnabled: async () => {},
});

export function FeatureFlagProvider({ workspaceId, children }) {
  const [aiEnabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!workspaceId) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    httpsCallable(functions, 'getWorkspaceFeatures')({ workspaceId })
      .then(res => { if (alive && res?.data) setEnabled(res.data.aiEnabled !== false); })
      // A settings read that fails must not switch AI off — that is the same
      // mistake in a different costume.
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [workspaceId]);

  const setAiEnabled = useCallback(async (next) => {
    if (!workspaceId) return;
    const previous = aiEnabled;
    setEnabled(next);           // optimistic, so the switch moves when pressed
    try {
      await httpsCallable(functions, 'setWorkspaceAiEnabled')({ workspaceId, enabled: next });
    } catch (e) {
      // Put it back rather than showing a state the server does not hold —
      // which is the whole bug this replaced.
      setEnabled(previous);
      throw e;
    }
  }, [workspaceId, aiEnabled]);

  return (
    <FeatureFlagContext.Provider value={{ aiEnabled, loading, setAiEnabled }}>
      {children}
    </FeatureFlagContext.Provider>
  );
}

export function useFeatureFlags() {
  return useContext(FeatureFlagContext);
}
